import React, { Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import LegacyTelehealthCallRedirect from '@/pages/telehealth/call/LegacyCallRedirect';

import AppConfig from '@/AppConfig';

import { Layout } from '@/components/Common/Layout';
import ErrorScreen from '@/components/Common/ErrorScreen/ErrorScreen';
import BaseElement from '@/pages/BaseElement';
import { Test } from '@/pages';
import AddFamilyMembers from '@/pages/add-family-member';
import ReviewDetails from '@/pages/review-details';
import RequestCreated from '@/pages/request-created';
import DisableTextSelectionWrapper from '@/components/Common/Wrapper/DisableTextSelectionWrapper';
import BasePromo from '@/pages/promos/BasePromo';
import BaseCult from '@/pages/promos/cult-fit/BaseCult';
import { healthInsuranceRoutes } from './health-insurance-routes';
import { superTopupRoutes } from './super-top-up-routes';

const Homepage = React.lazy(() => import('@/pages/homepage'));
const CreateClaim = React.lazy(() => import('@/pages/create'));
const ClaimsList = React.lazy(() => import('@/pages/list'));
const StatusPage = React.lazy(() => import('@/pages/status'));
const UploadPage = React.lazy(() => import('@/pages/upload/UploadPage'));
const StepDecider = React.lazy(() => import('@/pages/upload/StepDecider'));
const BaseUpload = React.lazy(() => import('@/pages/upload/BaseUpload'));
const NetworkHospitalsV2 = React.lazy(
  () => import('@/pages/network-hospitals/v2/index.tsx'),
);

const CultLandingPage = React.lazy(
  () => import('@/pages/promos/cult-fit/LandingPage'),
);
const CultCheckoutPage = React.lazy(
  () => import('@/pages/promos/cult-fit/CheckoutPage'),
);
const CultCheckoutStatusPage = React.lazy(
  () => import('@/pages/promos/cult-fit/CheckoutStatusPage'),
);

const BaseTelehealthV2 = React.lazy(
  () => import('@/pages/telehealth/v2/BaseTelehealthV2'),
);

const TelehealthHomeV2 = React.lazy(
  () => import('@/pages/telehealth/v2/index'),
);

const TelehealthCallV2 = React.lazy(
  () => import('@/pages/telehealth/v2/call/CallV2'),
);

const TelehealthFeedback = React.lazy(
  () => import('@/pages/telehealth/feedback/index.tsx'),
);

const TelehealthRejoinCallPage = React.lazy(
  () => import('@/pages/telehealth/call/rejoin/index.tsx'),
);

// Loaded outside the retail/health-insurance provider tree so that
// unauthenticated WebView payment gateway returns do not trigger
// PromoProvider or HealthInsuranceContextProvider authenticated queries.
const RhiPostPayment = React.lazy(
  () => import('@/pages/health-insurance/post-payment/index'),
);
const RhiPaymentFailed = React.lazy(
  () => import('@/pages/health-insurance/payment-failed/index'),
);
const RhiPaymentReturn = React.lazy(
  () => import('@/pages/health-insurance/payment-return'),
);

// Outside the retail/super-top-up journey tree for the same reason: a payment gateway return
// can arrive without a session, and the benefits page needs no journey state.
const StuPaymentReturn = React.lazy(
  () => import('@/pages/super-top-up/payment-return'),
);
const StuBenefits = React.lazy(() => import('@/pages/super-top-up/benefits'));

const suspenseFallback = (
  <Layout.Main
    headerProps={{
      hideBack: true,
    }}
    loading
  />
);

const router = createBrowserRouter([
  {
    path: AppConfig.BASE_PATH,
    element: (
      <Suspense fallback={suspenseFallback}>
        <BaseElement />
      </Suspense>
    ),
    errorElement: (
      <ErrorScreen layoutProps={{ headerProps: { hidePageHeader: true } }} />
    ),
    children: [
      {
        path: '',
        async lazy() {
          return {
            element: <Homepage />,
          };
        },
      },
      {
        path: 'claims',
        children: [
          {
            path: 'create',
            element: <CreateClaim />,
          },
          {
            path: 'all',
            element: <ClaimsList />,
          },
          {
            path: ':claimId',
            children: [
              {
                path: '',
                element: <StatusPage />,
              },
              {
                path: 'upload',
                element: <BaseUpload />,
                children: [
                  {
                    path: '',
                    element: <StepDecider />,
                  },
                  {
                    path: ':step',
                    element: <UploadPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'network-hospitals',
        element: <NetworkHospitalsV2 />,
      },
      {
        path: 'family-addition',
        element: (
          <DisableTextSelectionWrapper>
            <Outlet />
          </DisableTextSelectionWrapper>
        ),
        children: [
          {
            path: 'add-family-member',
            element: <AddFamilyMembers />,
          },
          {
            path: 'review-details',
            element: <ReviewDetails />,
          },
          {
            path: 'request-created',
            element: <RequestCreated />,
          },
        ],
      },
      // These two routes are intentionally placed outside the retail/BasePromo
      // subtree. React Router v6 prefers routes with more static path segments
      // over shorter parent paths, and since this flat entry (3 segments)
      // outscores the `retail` parent (1 segment), it is matched first.
      {
        path: 'retail/health-insurance/post-payment',
        element: <RhiPostPayment />,
      },
      {
        path: 'retail/health-insurance/payment-failed',
        element: <RhiPaymentFailed />,
      },
      {
        path: 'retail/health-insurance/success',
        element: <RhiPaymentReturn />,
      },
      {
        path: 'retail/health-insurance/failure',
        element: <RhiPaymentReturn />,
      },
      {
        path: 'retail/super-top-up/success',
        element: <StuPaymentReturn />,
      },
      {
        path: 'retail/super-top-up/failure',
        element: <StuPaymentReturn />,
      },
      {
        path: 'retail/super-top-up/benefits',
        element: <StuBenefits />,
      },
      {
        path: 'retail',
        element: <BasePromo />,
        children: [
          {
            path: 'cult-fit',
            element: <BaseCult />,
            children: [
              {
                path: '',
                element: <CultLandingPage />,
              },
              {
                path: 'checkout',
                element: <CultCheckoutPage />,
              },
              {
                path: 'status',
                element: <CultCheckoutStatusPage />,
              },
            ],
          },
          ...healthInsuranceRoutes,
          ...superTopupRoutes,
        ],
      },
      {
        path: 'test',
        element: <Test />,
      },
      {
        path: '/telehealth',
        element: <BaseTelehealthV2 />,
        errorElement: (
          <ErrorScreen
            layoutProps={{ headerProps: { hidePageHeader: true } }}
          />
        ),
        children: [
          { path: '', element: <TelehealthHomeV2 /> },
          { path: 'call', element: <LegacyTelehealthCallRedirect /> },
          { path: 'call/rejoin', element: <TelehealthRejoinCallPage /> },
          { path: 'v2/call', element: <TelehealthCallV2 /> },
          { path: 'feedback', element: <TelehealthFeedback /> },
        ],
      },
    ],
  },
]);

export { router };
